import React from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import IconButton from "../../../../../../components/Button/IconButton";
import Container from "../../../../../../components/Container";
import Icon from "../../../../../../components/Icon/Icon";
import Spacer from "../../../../../../components/Spacer";
// components
import Text from "../../../../../../components/Text";
import Tooltip from "../../../../../../components/Tooltip";

// store
import useReportStore from "../../store";
import { ChartType } from "../../store/enums";

import css from "./VisualizePanel.module.scss";
import { chartMetaData } from "./chartConfig";

const defaultChartOptions = [
	ChartType.Table,
	ChartType.BarChart,
	ChartType.ColumnChart,
	ChartType.ScatterChart,
	ChartType.HistogramChart,
	ChartType.NineBox,
	ChartType.TrendlineChart,
];

const VisualizePanel = () => {
	const [getAvailableCharts, selectedChart, setSelectedChart, isSummarizeRowsView] = useReportStore((state) => [
		state.getAvailableCharts,
		state.selectedChart,
		state.setSelectedChart,
		state.isSummarizeRowsView,
	]);

	const chartOptions = defaultChartOptions;
	const availableCharts = new Set([...getAvailableCharts(), ChartType.Table]);

	const handleChartSelect = (chart: ChartType) => {
		setSelectedChart(chart);
	};

	const Header = (
		<>
			<div className={css.panelHeader}>
				<Container>
					<Text size="paragraph-2" color="neutral-9" weight="semi-bold">
						Visualizations
					</Text>
					<Tooltip
						title="Visualize"
						titleBottomLine
						message={
							"Ability to switch between the table view and additional charts based on your selected data and summarize panel settings"
						}
					>
						<Icon icon={faCircleInfo} size="xx-small" color="neutral-7" />
					</Tooltip>
				</Container>
			</div>
			<Spacer height={2} />
		</>
	);

	const ChartOptions = (
		<>
			<div className={css.chartOptionsContainer} data-testid="chart-options-container">
				{chartOptions.map((c) => {
					const isActive = selectedChart === c;
					return (
						<Tooltip key={c} message={chartMetaData[c].title || c}>
							<IconButton
								disabled={!availableCharts.has(c) || (!isSummarizeRowsView && c !== ChartType.Table)}
								className={isActive ? css.active : ""}
								icon={chartMetaData[c].icon}
								square
								testId={c + "-button"}
								onClick={() => {
									handleChartSelect(c);
								}}
							/>
						</Tooltip>
					);
				})}
			</div>
			<Spacer height={2} />
		</>
	);

	const allDisabled = chartOptions.every((c) => c === ChartType.Table || !availableCharts.has(c));

	const Description = (
		<>
			{selectedChart === ChartType.Table && !isSummarizeRowsView ? (
				<div className={css.bannerWrapper}>
					<Icon size="x-small" className={css.alertIcon} icon={faTriangleExclamation} color="primary-5" />
					<Text size="caption" color="neutral-9" weight="regular">
						To access additional chart types, you must first summarize your data.
					</Text>
				</div>
			) : (
				allDisabled && (
					<div className={css.bannerWrapper}>
						<Icon size="x-small" className={css.alertIcon} icon={faTriangleExclamation} color="primary-5" />
						<Text size="caption" color="neutral-9" weight="regular">
							There are no additional chart types for the selected data.
						</Text>
					</div>
				)
			)}
			<Text size="paragraph-2" color="neutral-9" weight="semi-bold">
				{selectedChart}
			</Text>
			<Text size="caption" color="neutral-7" weight="regular">
				{chartMetaData[selectedChart]?.description}
			</Text>
		</>
	);

	const OptionsSection = (
		<>
			{selectedChart !== ChartType.Table && (
				<>
					<div className={css.optionsTextContainer}>
						<Text size="paragraph-2" color="neutral-9" weight="semi-bold" className={css.liner}>
							Settings
						</Text>
					</div>
					<Spacer height={1} />
				</>
			)}
			<div className={css.optionsSection}>{chartMetaData[selectedChart]?.getBaseOptions?.()}</div>
		</>
	);

	return (
		<div className={css.visualizePanel} data-testid="VisualizePanelOptions">
			<div className={css.visualizePanelContainer}>
				<div className={css.topSectionWrapper}>
					{/* Header */}
					{Header}
					{/* Chart option buttons */}
					{ChartOptions}
					{/* Description */}
					{Description}
				</div>
				{/* Chart Options Select Inputs */}
				{OptionsSection}
			</div>
		</div>
	);
};

export default VisualizePanel;
